import type { FeedbackType, PortalNoteView, WidgetConfig } from "@anota/shared";
import { accentText, onAccent } from "./color";
import { FEEDBACK_TYPES, LIMITS, type LauncherStyle } from "@anota/shared/limits";
import type { Ref } from "preact";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "preact/hooks";
import { ApiError } from "../api";
import type { Backend } from "../backend";
import { consoleEntries } from "../capture/console";
import { networkEntries } from "../capture/network";
import { trailEntries } from "../capture/trail";
import { containerOf, describeElement, isPointTarget, type ViewportRect } from "../capture/element";
import { environment } from "../capture/environment";
import { strings, type Lang, type Strings } from "../i18n";
import { withTimeout } from "../shot-loader";
import { hideBar, isBarHidden, loadReporter, saveReporter } from "../storage";
import { Draw } from "./Draw";
import { MyNotes } from "./MyNotes";
import { Pins } from "./Pins";

export const HOST_ID = "anota-root";

export interface AppProps {
  config: WidgetConfig;
  /** Servidor de Anota o envío propio del sitio (paquete npm). */
  backend: Backend;
  host: HTMLElement;
  lang: Lang;
  /** Para `Anota.open()` y `Anota.openNotes()`: el widget se registra aquí al montarse. */
  bind: (controls: Controls) => void;
}

export interface Controls {
  /** Empezar a anotar (modo "elegir"). */
  open: () => void;
  /** «Tus notas» (solo con servidor de Anota). */
  openNotes: () => void;
}

type Mode = "idle" | "picking" | "form" | "mine";
interface Target {
  el: Element;
  click: { x: number; y: number };
  /** Si dibujó un área al elegir (con el ratón): su recuadro en la ventana. */
  area?: ViewportRect;
}

type Fraction = { x: number; y: number; width: number; height: number };

/** Un área guardada en fracciones de la caja de su bloque, en coordenadas de la ventana de ahora. */
const areaNow = (el: Element, a: Fraction) => {
  const r = el.getBoundingClientRect();
  return { x: r.left + a.x * r.width, y: r.top + a.y * r.height, width: a.width * r.width, height: a.height * r.height };
};

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** El símbolo de Anota (brand/): un pin que también es la punta de una pluma. Agujero y ranura del color del botón. */
function MarkIcon() {
  return (
    <svg width="14" height="19" viewBox="12 6 40 55" aria-hidden="true">
      <path d="M32 61C26.5 53 12 40 12 26a20 20 0 0 1 40 0c0 14-14.5 27-20 35z" fill="currentColor" />
      <circle cx="32" cy="26" r="7" style={{ fill: "var(--accent)" }} />
      <rect x="30.75" y="29" width="2.5" height="34" style={{ fill: "var(--accent)" }} />
    </svg>
  );
}

export const SHORTCUT = "Alt+Shift+A";
const MAC = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.userAgent);
// Pantalla táctil sin ratón: se toca, no se hace clic.
const COARSE = typeof matchMedia !== "undefined" && matchMedia("(pointer: coarse)").matches;
/** Quien pidió menos movimiento: las salidas no esperan a su animación. */
const reducedMotion = () => typeof matchMedia !== "undefined" && matchMedia("(prefers-reduced-motion: reduce)").matches;

/**
 * Lo que abre el widget, según cuánto se quiera ver en la web (de más a menos): barra de revisión (abajo), botón
 * con texto, botón redondo, pestaña lateral u oculto. La barra se puede ocultar: entonces queda la pestaña.
 */
function Launcher({
  style,
  t,
  buttonRef,
  barHidden,
  onHideBar,
  onOpen,
}: {
  style: LauncherStyle;
  t: Strings;
  buttonRef: Ref<HTMLButtonElement>;
  barHidden: boolean;
  onHideBar: () => void;
  onOpen: () => void;
}) {
  const title = `${t.launchTitle} · ${SHORTCUT}`;
  const common = { ref: buttonRef, type: "button" as const, title, "aria-label": t.launchTitle, "aria-keyshortcuts": SHORTCUT, onClick: onOpen };
  if (style === "hidden") return null;
  if (style === "bar" && !barHidden) {
    return (
      <div class="review-bar" role="region" aria-label="Anota">
        <MarkIcon />
        <span>{t.reviewBar}</span>
        <button class="rb-go" {...common}>
          {t.launch}
        </button>
        <button
          type="button"
          class="rb-x"
          title={t.hideBar}
          aria-label={t.hideBar}
          onClick={onHideBar}
        >
          ✕
        </button>
      </div>
    );
  }
  if (style === "bar" || style === "tab") {
    return (
      <button class="tab-launch" {...common}>
        <MarkIcon />
        <span>{t.launch}</span>
      </button>
    );
  }
  if (style === "icon") {
    return (
      <button class="launcher round" {...common}>
        <MarkIcon />
      </button>
    );
  }
  return (
    <button class="launcher" {...common}>
      <MarkIcon />
      {t.launch}
    </button>
  );
}

/** Caja sobre un elemento de la página, en coordenadas de la ventana (sigue el scroll). */
function useRect(el: Element | null) {
  const [rect, setRect] = useState<DOMRect | null>(el ? el.getBoundingClientRect() : null);
  useEffect(() => {
    if (!el) return setRect(null);
    let frame = 0;
    const update = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => setRect(el.getBoundingClientRect()));
    };
    update();
    addEventListener("scroll", update, true);
    addEventListener("resize", update);
    return () => {
      cancelAnimationFrame(frame);
      removeEventListener("scroll", update, true);
      removeEventListener("resize", update);
    };
  }, [el]);
  return rect;
}

function Box({ el, tag, fixed }: { el: Element | null; tag?: string; fixed?: boolean }) {
  const r = useRect(el);
  if (!r) return null;
  return (
    <div class={fixed ? "hl fixed" : "hl"} style={{ top: `${r.top}px`, left: `${r.left}px`, width: `${r.width}px`, height: `${r.height}px` }}>
      {tag && <span class="hl-tag">{tag}</span>}
    </div>
  );
}

/** Pin de una nota sobre un punto. Con `el` y `point` (0 a 1 dentro del bloque) sigue al bloque al hacer scroll. */
/** Recuadro de un área: el que se dibuja al elegir, o el guardado (sigue al bloque al hacer scroll). */
function AreaBox({ rect, el, area }: { rect?: ViewportRect; el?: Element; area?: Fraction }) {
  const r = useRect(el ?? null);
  const box = r && area ? { left: r.left + area.x * r.width, top: r.top + area.y * r.height, width: area.width * r.width, height: area.height * r.height } : rect;
  if (!box) return null;
  return <div class={el ? "area-box fixed" : "area-box"} style={{ left: `${box.left}px`, top: `${box.top}px`, width: `${box.width}px`, height: `${box.height}px` }} />;
}

function Pin({ x, y, el, point }: { x?: number; y?: number; el?: Element; point?: { x: number; y: number } }) {
  const r = useRect(el ?? null);
  const left = r && point ? r.left + point.x * r.width : x;
  const top = r && point ? r.top + point.y * r.height : y;
  if (left === undefined || top === undefined) return null;
  return <div class="pin" style={{ left: `${left}px`, top: `${top}px` }} />;
}

/** Eventos del sitio que se anulan mientras se elige: un clic no debe abrir enlaces ni menús. */
const BLOCKED = ["pointerdown", "mousedown", "pointerup", "mouseup", "dblclick", "auxclick", "contextmenu"] as const;

function Picker({
  host,
  t,
  onPick,
  onWhole,
  onMine,
  news,
  onCancel,
}: {
  host: HTMLElement;
  t: Strings;
  onPick: (target: Target) => void;
  onWhole: () => void;
  /** Si ya dejó notas (o tiene enlace de revisión): «Tus notas» en la barra. */
  onMine?: () => void;
  /** Cuántas tienen novedades (respuesta o cambio de estado sin ver). */
  news: number;
  onCancel: () => void;
}) {
  // Con el teclado: la barra lo explica y el primer botón tiene el foco al empezar.
  const [keys, setKeys] = useState(false);
  const firstButton = useRef<HTMLButtonElement>(null);
  useEffect(() => firstButton.current?.focus(), []);
  const [hover, setHover] = useState<{ el: Element; x: number; y: number } | null>(null);
  const [drag, setDrag] = useState<ViewportRect | null>(null);
  // Con ratón se puede arrastrar para marcar un área; en pantallas táctiles, arrastrar es desplazarse.
  const fine = typeof matchMedia === "function" && matchMedia("(pointer: fine)").matches;
  // Layout y no useEffect (que espera a después de pintar): desde que se ve la barra, Esc y los clics
  // ya los atiende el widget, aunque lleguen enseguida.
  useLayoutEffect(() => {
    const root = document.documentElement;
    const cursor = root.style.cursor;
    root.style.cursor = "crosshair";
    const ours = (e: Event) => e.composedPath().includes(host);
    // También vale <html> (debajo de un <body> corto): es una nota sobre un punto de la página.
    const at = (x: number, y: number) => {
      const el = document.elementFromPoint(x, y);
      return el && el !== host && !host.contains(el) ? el : null;
    };
    const block = (e: Event) => {
      if (ours(e)) return;
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
    };
    let start: { x: number; y: number } | null = null;
    let dragging = false;
    const rectFrom = (a: { x: number; y: number }, b: { x: number; y: number }): ViewportRect => ({
      left: Math.min(a.x, b.x),
      top: Math.min(a.y, b.y),
      width: Math.abs(b.x - a.x),
      height: Math.abs(b.y - a.y),
    });
    const onMove = (e: PointerEvent) => {
      if (ours(e)) return;
      if (start && e.buttons & 1) {
        if (!dragging && Math.hypot(e.clientX - start.x, e.clientY - start.y) > 8) dragging = true;
        if (dragging) {
          setHover(null);
          setDrag(rectFrom(start, { x: e.clientX, y: e.clientY }));
          return;
        }
      }
      const el = at(e.clientX, e.clientY);
      setHover(el ? { el, x: e.clientX, y: e.clientY } : null);
    };
    const onPress = (e: PointerEvent) => {
      if (ours(e)) return;
      block(e);
      if (e.type === "pointerdown") {
        start = e.pointerType === "touch" || e.button !== 0 ? null : { x: e.clientX, y: e.clientY };
        dragging = false;
        return;
      }
      if (start && dragging) {
        const area = rectFrom(start, { x: e.clientX, y: e.clientY });
        start = null;
        dragging = false;
        setDrag(null);
        // El mouseup y el clic que vienen detrás no deben llegar a la web (el selector ya se habrá ido).
        const swallow = (ev: Event) => {
          ev.preventDefault();
          ev.stopPropagation();
          ev.stopImmediatePropagation();
        };
        addEventListener("mouseup", swallow, true);
        addEventListener("click", swallow, true);
        setTimeout(() => {
          removeEventListener("mouseup", swallow, true);
          removeEventListener("click", swallow, true);
        }, 0);
        onPick({ el: containerOf(area, host), click: { x: area.left + area.width / 2, y: area.top + area.height / 2 }, area });
        return;
      }
      start = null;
    };
    const onClick = (e: MouseEvent) => {
      if (ours(e)) return;
      block(e);
      const el = at(e.clientX, e.clientY);
      if (el) onPick({ el, click: { x: e.clientX, y: e.clientY } });
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Tab") setKeys(true);
      if (e.key === "Enter") {
        // Lo enfocado de la web (no lo nuestro): se elige, como con un clic.
        const el = document.activeElement;
        if (el instanceof HTMLElement && el !== document.body && !ours(e)) {
          e.preventDefault();
          e.stopPropagation();
          const r = el.getBoundingClientRect();
          onPick({ el, click: { x: r.left + r.width / 2, y: r.top + r.height / 2 } });
        }
        return;
      }
      if (e.key !== "Escape") return;
      e.preventDefault();
      e.stopPropagation();
      onCancel();
    };
    // Con el teclado, lo enfocado se resalta como al pasar el ratón.
    const onFocus = (e: FocusEvent) => {
      const el = e.target;
      if (!(el instanceof HTMLElement) || el === document.body || host.contains(el)) return;
      const r = el.getBoundingClientRect();
      setHover({ el, x: r.left + r.width / 2, y: r.top + r.height / 2 });
    };
    addEventListener("focusin", onFocus, true);
    const handlers = BLOCKED.map((type) => [type, type === "pointerdown" || type === "pointerup" ? onPress : block] as const);
    addEventListener("pointermove", onMove, true);
    for (const [type, fn] of handlers) addEventListener(type, fn as EventListener, true);
    addEventListener("click", onClick, true);
    addEventListener("keydown", onKey, true);
    return () => {
      removeEventListener("pointermove", onMove, true);
      for (const [type, fn] of handlers) removeEventListener(type, fn as EventListener, true);
      removeEventListener("click", onClick, true);
      removeEventListener("keydown", onKey, true);
      removeEventListener("focusin", onFocus, true);
      root.style.cursor = cursor;
    };
  }, []);
  return (
    <>
      {drag && <AreaBox rect={drag} />}
      <div class="bar" role="status">
        <span>{keys ? t.pickKeys : fine ? t.pickHintDrag : COARSE ? t.pickHintTouch : t.pickHint}</span>
        <button type="button" ref={firstButton} onClick={onWhole}>
          {t.wholePage}
        </button>
        {onMine && (
          <button type="button" onClick={onMine}>
            {t.mine}
            {news > 0 && <span class="count">{news}</span>}
          </button>
        )}
        <button type="button" onClick={onCancel}>
          {t.cancel}
        </button>
      </div>
      {hover && !drag &&
        // En un bloque grande se marca el punto exacto, no el bloque entero.
        (isPointTarget(hover.el) ? <Pin x={hover.x} y={hover.y} /> : <Box el={hover.el} tag={hover.el.tagName.toLowerCase()} />)}
    </>
  );
}

function Form({ app, t, target, onDone, onCancel }: { app: AppProps; t: Strings; target: Target | null; onDone: (n?: number) => void; onCancel: () => void }) {
  const { settings } = app.config;
  const remembered = useMemo(loadReporter, []);
  const [text, setText] = useState("");
  const [type, setType] = useState<FeedbackType>("change");
  // Si llegó con un enlace de revisión, su nombre ya viene en el enlace ("Ana · cliente").
  const [name, setName] = useState(remembered.name || app.config.reviewer?.label || "");
  const [email, setEmail] = useState(remembered.email ?? "");
  const [withShot, setWithShot] = useState(settings.screenshots);
  const [phase, setPhase] = useState<"edit" | "capturing" | "sending">("edit");
  const [error, setError] = useState<string | null>(null);
  const area = useRef<HTMLTextAreaElement>(null);
  // El elemento se describe al elegirlo: así queda como estaba cuando el cliente lo señaló.
  const anchor = useMemo(() => (target ? describeElement(target.el, target.click, target.area) : undefined), [target]);
  // Nota sobre un punto de un bloque grande: se marca y se captura el punto, no el bloque.
  const point = anchor?.kind === "point" ? anchor.point : undefined;
  // Nota sobre un área dibujada: se recuadra el área, no el bloque.
  const zone = anchor?.kind === "area" ? anchor.area : undefined;
  // La captura con las marcas que dibujó (sustituye a la captura normal) y la que se está marcando.
  const [marked, setMarked] = useState<Blob | null>(null);
  const [drawing, setDrawing] = useState<Blob | null>(null);

  useLayoutEffect(() => area.current?.focus(), []);

  // El panel no debe tapar lo señalado: si lo tapa, al otro lado; si aún lo tapa (pantalla estrecha), arriba.
  const panel = useRef<HTMLDivElement>(null);
  const [place, setPlace] = useState("");
  useLayoutEffect(() => {
    const box = panel.current?.getBoundingClientRect();
    if (!box || !target) return;
    const r = target.el.getBoundingClientRect();
    const a = zone ? areaNow(target.el, zone) : null;
    const spot = a
      ? { left: a.x, right: a.x + a.width, top: a.y, bottom: a.y + a.height }
      : point
        ? { left: r.left + point.x * r.width - 16, right: r.left + point.x * r.width + 16, top: r.top + point.y * r.height - 16, bottom: r.top + point.y * r.height + 16 }
        : r;
    const covers = (b: { left: number; right: number; top: number; bottom: number }) =>
      spot.left < b.right && spot.right > b.left && spot.top < b.bottom && spot.bottom > b.top;
    if (!covers(box)) return;
    const mirrored = { left: innerWidth - box.right, right: innerWidth - box.left, top: box.top, bottom: box.bottom };
    const raise = (b: typeof mirrored) => ({ ...b, top: 20, bottom: 20 + box.height });
    const other = settings.position === "bottom-left" ? "side-right" : "side-left";
    if (!covers(mirrored)) setPlace(other);
    else if (!covers(raise(box))) setPlace("at-top");
    else if (!covers(raise(mirrored))) setPlace(`${other} at-top`);
  }, []);

  /** La captura de la ventana de ahora (el cliente pudo hacer scroll mientras escribía), con lo señalado marcado. */
  async function takeShot(): Promise<Blob | null> {
    try {
      const capture = await withTimeout(app.backend.loadCapture(), 8000);
      if (!capture) return null;
      const r = target?.el.getBoundingClientRect();
      const a = target && zone ? areaNow(target.el, zone) : null;
      return await withTimeout(
        capture({
          exclude: [
            `#${HOST_ID}`,
            'input[type="password"]',
            '[autocomplete^="cc-"]',
            '[autocomplete="one-time-code"]',
            "[data-anota-mask]",
            ...settings.mask,
          ],
          highlight: a ?? (r && !point ? { x: r.left, y: r.top, width: r.width, height: r.height } : undefined),
          pin: r && point ? { x: r.left + point.x * r.width, y: r.top + point.y * r.height } : undefined,
          color: settings.color,
        }),
        12000
      );
    } catch (err) {
      console.warn("[Anota] No se pudo hacer la captura:", err);
      return null;
    }
  }

  /** "Marcar en la captura": se captura ya y se abre el editor de marcas. */
  async function openDraw() {
    if (phase !== "edit") return;
    setPhase("capturing");
    const shot = await takeShot();
    setPhase("edit");
    if (shot) setDrawing(shot);
    else setError(t.error);
  }

  async function submit() {
    if (phase !== "edit") return;
    const description = text.trim();
    if (!description) {
      setError(t.required);
      area.current?.focus();
      return;
    }
    if (email.trim() && !EMAIL.test(email.trim())) return setError(t.invalidEmail);
    setError(null);
    // El contexto, antes de la captura: lo que hace la propia captura (cargar imágenes…) no es del sitio.
    const context = { console: consoleEntries(), network: networkEntries(), trail: trailEntries() };

    // La que marcó a mano, si la hay; si no, la captura de ahora.
    let shot: Blob | null = withShot ? marked : null;
    if (withShot && !shot) {
      setPhase("capturing");
      shot = await takeShot();
    }

    setPhase("sending");
    try {
      const sent = await app.backend.submit(
        {
          type,
          description: description.slice(0, LIMITS.description),
          ...(anchor ? { anchor } : {}),
          environment: environment(),
          ...context,
          reporter: { ...(name.trim() ? { name: name.trim().slice(0, 80) } : {}), ...(email.trim() ? { email: email.trim() } : {}) },
        },
        shot
      );
      saveReporter({ name: name.trim(), email: email.trim() });
      onDone(sent.number);
    } catch (err) {
      // Los del servidor de Anota ya traen un mensaje para personas; los de un envío propio, al registro.
      if (!(err instanceof ApiError)) console.warn("[Anota] No se pudo enviar la nota:", err);
      setPhase("edit");
      setError(err instanceof ApiError ? err.message : t.error);
    }
  }

  const markButton = useRef<HTMLButtonElement>(null);
  const busy = phase !== "edit";
  const cancel = useRef(onCancel);
  cancel.current = onCancel;
  useEffect(() => {
    if (busy || drawing) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape" || e.defaultPrevented) return;
      e.preventDefault();
      cancel.current();
    };
    addEventListener("keydown", onKey);
    return () => removeEventListener("keydown", onKey);
  }, [busy, drawing]);
  return (
    <>
      {target && zone ? <AreaBox el={target.el} area={zone} /> : target && point ? <Pin el={target.el} point={point} /> : <Box el={target?.el ?? null} fixed />}
      {drawing && (
        <Draw
          image={drawing}
          color={settings.color}
          t={t}
          onDone={(blob) => {
            setMarked(blob);
            setDrawing(null);
            requestAnimationFrame(() => markButton.current?.focus());
          }}
          onCancel={() => {
            setDrawing(null);
            requestAnimationFrame(() => markButton.current?.focus());
          }}
        />
      )}
      <div ref={panel} class={place ? `panel ${place}` : "panel"} role="dialog" aria-modal="false" aria-labelledby="anota-title">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
        onKeyDown={(e) => {
          if (e.key === "Tab" && panel.current) {
            const all = [...panel.current.querySelectorAll<HTMLElement>("button:not([disabled]), textarea:not([disabled]), input:not([disabled]), a[href]")];
            const first = all[0];
            const last = all[all.length - 1];
            const here = (panel.current.getRootNode() as ShadowRoot).activeElement;
            if (e.shiftKey && here === first) {
              e.preventDefault();
              last?.focus();
            } else if (!e.shiftKey && here === last) {
              e.preventDefault();
              first?.focus();
            }
          } else if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
            e.preventDefault();
            void submit();
          }
        }}
      >
        <div class="head">
          <h2 id="anota-title">{t.newNote}</h2>
          <button type="button" class="x" aria-label={t.close} title={t.close} onClick={onCancel} disabled={busy}>
            ✕
          </button>
        </div>
        <p class="ctx">
          {anchor && zone
            ? t.areaPage
            : anchor && point
              ? t.pointPage
              : anchor
                ? anchor.text
                  ? `${t.about} «${anchor.text.length > 60 ? `${anchor.text.slice(0, 59)}…` : anchor.text}»`
                  : `${t.about} ${t.tagNames[anchor.tag] ?? t.tagOther}`
                : t.wholePageNote}
        </p>
        <textarea
          ref={area}
          value={text}
          maxLength={LIMITS.description}
          placeholder={t.placeholder}
          aria-label={t.placeholder}
          aria-invalid={error === t.required || undefined}
          aria-describedby={error ? "anota-error" : undefined}
          onInput={(e) => setText((e.currentTarget as HTMLTextAreaElement).value)}
          disabled={busy}
        />
        <fieldset class="chips">
          <legend>{t.typeLabel}</legend>
          {FEEDBACK_TYPES.map((k) => (
            <button type="button" class="chip" aria-pressed={type === k} onClick={() => setType(k)} disabled={busy}>
              {t.types[k]}
            </button>
          ))}
        </fieldset>
        {settings.askReporter && (
          <div class="who">
            <input value={name} placeholder={`${t.name} (${t.optional})`} aria-label={t.name} autocomplete="name" maxLength={80} onInput={(e) => setName((e.currentTarget as HTMLInputElement).value)} disabled={busy} />
            <input
              value={email}
              type="email"
              placeholder={`${t.email} (${t.optional})`}
              aria-label={t.email}
              autocomplete="email"
              maxLength={200}
              onInput={(e) => setEmail((e.currentTarget as HTMLInputElement).value)}
              disabled={busy}
            />
          </div>
        )}
        {settings.screenshots && (
          <div class="shot-row">
            <label class="check">
              <input type="checkbox" checked={withShot} onChange={(e) => setWithShot((e.currentTarget as HTMLInputElement).checked)} disabled={busy} />
              {marked && withShot ? t.drawMarked : t.screenshot}
            </label>
            {withShot && (
              <button ref={markButton} type="button" class="link" onClick={() => void openDraw()} disabled={busy}>
                {marked ? t.drawAgain : t.drawOn}
              </button>
            )}
          </div>
        )}
        {error && (
          <p class="err" id="anota-error" role="alert">
            {error}
          </p>
        )}
        <div class="actions">
          <span class="hint">{MAC ? t.shortcutMac : t.shortcut}</span>
          <div class="btns">
            <button type="button" class="btn" onClick={onCancel} disabled={busy}>
              {t.cancel}
            </button>
            <button type="submit" class="btn primary" disabled={busy}>
              {phase === "capturing" ? t.capturing : phase === "sending" ? t.sending : t.send}
            </button>
          </div>
        </div>
      </form>
      </div>
    </>
  );
}

export function App(props: AppProps) {
  const t = strings(props.lang);
  const [mode, setMode] = useState<Mode>("idle");
  const [target, setTarget] = useState<Target | null>(null);
  // El aviso de "¡Gracias!" lleva a «Tus notas» cuando las hay.
  const [toast, setToast] = useState<{ text: string; mine: boolean } | null>(null);
  // El aviso se queda mientras el ratón o el foco están en él.
  const [toastHeld, setToastHeld] = useState(false);
  const [toastOut, setToastOut] = useState(false);
  // Sube al enviar una nota: los pines vuelven a pedir las de la página (la nueva incluida).
  const [refresh, setRefresh] = useState(0);
  const launcher = useRef<HTMLButtonElement>(null);

  const mine = props.backend.mine;
  // Notas con novedades: el aviso junto al botón lleva a «Tus notas».
  const [news, setNews] = useState(0);
  // Las últimas que se contaron: la × del aviso las da por vistas.
  const counted = useRef<PortalNoteView[]>([]);
  useEffect(() => {
    if (!mine?.any()) return;
    let live = true;
    // Al cargar la página, siempre (solo quien ya dejó notas); al volver a la pestaña, como mucho una vez por minuto.
    let last = 0;
    const count = () => {
      last = Date.now();
      void mine.list().then((items) => {
        if (!live) return;
        counted.current = items;
        setNews(items.filter(mine.isNew).length);
      }, () => {});
    };
    count();
    // Al volver a la pestaña, lo que haya contestado el equipo mientras tanto.
    const onVisible = () => document.visibilityState === "visible" && Date.now() - last > 60_000 && count();
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      live = false;
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [refresh]);
  useLayoutEffect(
    () =>
      props.bind({
        open: () => setMode("picking"),
        openNotes: () => {
          if (mine) goTo("mine");
        },
      }),
    []
  );
  useEffect(() => {
    if (!toast || toastHeld) return;
    const id = setTimeout(() => {
      // Se va con una salida corta.
      setToastOut(true);
      setTimeout(() => {
        setToast((t) => (t === toast ? null : t));
        setToastOut(false);
      }, reducedMotion() ? 0 : 160);
    }, toast.mine ? 8000 : 4500);
    return () => clearTimeout(id);
  }, [toast, toastHeld]);

  // Atajo para abrirlo en cualquier estilo (en "oculto", es la forma de abrirlo). No se le roba a quien escribe.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!e.altKey || !e.shiftKey || e.ctrlKey || e.metaKey || e.code !== "KeyA") return;
      const el = e.composedPath()[0];
      if (el instanceof HTMLElement && (el.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName))) return;
      e.preventDefault();
      setMode((m) => (m === "idle" ? "picking" : m));
    };
    addEventListener("keydown", onKey);
    return () => removeEventListener("keydown", onKey);
  }, []);

  const color = props.config.settings.color;
  const [barHidden, setBarHidden] = useState(isBarHidden);

  // El panel (o la barra de elegir) se va con una salida corta antes de cerrarse.
  const [leaving, setLeaving] = useState(false);
  const leaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** Abrir otra cosa (p. ej. «Tus notas» desde el aviso) cancela una salida a medias: si no, la cerraría. */
  const goTo = (next: Mode) => {
    if (leaveTimer.current) clearTimeout(leaveTimer.current);
    leaveTimer.current = null;
    setLeaving(false);
    setMode(next);
  };
  const reset = () => {
    const done = () => {
      leaveTimer.current = null;
      setLeaving(false);
      setMode("idle");
      setTarget(null);
      requestAnimationFrame(() => launcher.current?.focus());
    };
    if (mode === "idle" || reducedMotion()) return done();
    if (leaveTimer.current) return;
    setLeaving(true);
    leaveTimer.current = setTimeout(done, 140);
  };

  return (
    <div
      class="anota"
      lang={t.lang}
      style={{ "--accent": color, "--on-accent": onAccent(color), "--accent-text": accentText(color) }}
      data-position={props.config.settings.position}
      data-launcher={props.config.settings.launcher}
      data-bar={mode === "idle" && props.config.settings.launcher === "bar" && !barHidden ? "" : undefined}
      data-leaving={leaving ? "" : undefined}
    >
      {mode === "idle" && (
        <Launcher
          style={props.config.settings.launcher}
          t={t}
          buttonRef={launcher}
          barHidden={barHidden}
          onHideBar={() => {
            hideBar();
            setBarHidden(true);
          }}
          onOpen={() => setMode("picking")}
        />
      )}
      {/* Si el sitio lo ocultó, tampoco el aviso: lo abrirá él (Anota.openNotes()). */}
      {mode === "idle" && news > 0 && props.config.settings.launcher !== "hidden" && (
        <div class="news-chip">
          <button type="button" class="news-open" onClick={() => goTo("mine")}>
            <span class="dot" aria-hidden="true" />
            {t.news(news)}
          </button>
          {/* Descartarlo: quedan vistas hasta que el equipo vuelva a contestar o cambie un estado. */}
          <button
            type="button"
            class="news-x"
            aria-label={t.dismissNews}
            title={t.dismissNews}
            onClick={() => {
              mine?.seenAll(counted.current);
              setNews(0);
            }}
          >
            ×
          </button>
        </div>
      )}
      {/* Con enlace de revisión: las notas de esta página, con su estado (se apartan mientras se elige o se escribe). */}
      {mode === "idle" && props.backend.notes && <Pins api={props.backend.notes} t={t} refresh={refresh} />}
      {mode === "picking" && (
        <Picker
          host={props.host}
          t={t}
          onPick={(picked) => {
            setTarget(picked);
            setMode("form");
          }}
          onWhole={() => {
            setTarget(null);
            setMode("form");
          }}
          onMine={mine?.any() ? () => setMode("mine") : undefined}
          news={news}
          onCancel={reset}
        />
      )}
      {mode === "form" && (
        <Form
          app={props}
          t={t}
          target={target}
          onDone={(n) => {
            setToast({ text: t.sent(n), mine: Boolean(mine?.any()) });
            setRefresh((r) => r + 1);
            reset();
          }}
          onCancel={reset}
        />
      )}
      {mode === "mine" && mine && <MyNotes api={mine} t={t} project={location.hostname || props.config.project.name} onClose={reset} onNews={setNews} />}
      {toast && (
        <div
          class={toastOut ? "toast out" : "toast"}
          role="status"
          onMouseEnter={() => setToastHeld(true)}
          onMouseLeave={() => setToastHeld(false)}
          onFocusIn={() => setToastHeld(true)}
          onFocusOut={() => setToastHeld(false)}
        >
          <svg class="tick" width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
            <path d="m3.5 8.5 3 3 6-7" />
          </svg>
          {toast.text}
          {toast.mine && (
            <button
              type="button"
              class="link"
              onClick={() => {
                setToast(null);
                goTo("mine");
              }}
            >
              {t.seeMine}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
